import { DialogContentText } from "@material-ui/core";
import ActionDialog from "@saleor/components/ActionDialog";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import useNotifier from "@saleor/hooks/useNotifier";
import { useExportRemittanceOrders } from "@saleor/remittanceRunner/mutations";
import { useExportRemittanceOrderFile } from "@saleor/remittanceRunner/queries";
import { getMomentDate } from "@saleor/remittanceRunner/utils/handlers";
import {
  ExportScope,
  FileTypesEnum,
  JobStatusEnum
} from "@saleor/types/globalTypes";
import React, { FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { RemittanceRunnerListUrlQueryParams } from "../../url";

export interface RemittanceRunnerDownloadDialogProps {
  params: RemittanceRunnerListUrlQueryParams;
  totalRows: number;
  filters: any;
  closeModal: () => void;
}

const RemittanceRunnerDownloadDialog: FC<RemittanceRunnerDownloadDialogProps> = props => {
  const { params, totalRows, filters, closeModal } = props;

  const intl = useIntl();
  const notify = useNotifier();

  const MAX_DOWNLOAD_RETRIES = 5;

  const [exportFileID, setExportFileID] = React.useState<string | undefined>();
  const [downloadStatus, setDownloadStatus] = React.useState<
    ConfirmButtonTransitionState
  >("default");
  const [dialogButtonDisabled, setDialogButtonDisabled] = React.useState<
    boolean
  >(false);
  const [downloadRequestCtr, setDownloadRequestCtr] = React.useState(0);

  const cancelExport = () => {
    closeModal();
    setDialogButtonDisabled(false);
    setDownloadStatus("default");
    setExportFileID(null);
  };

  const [exportRemittanceOrders] = useExportRemittanceOrders({
    onCompleted: ({ exportRemittanceOrders }) => {
      if (exportRemittanceOrders?.errors?.length === 0) {
        setExportFileID(exportRemittanceOrders?.exportFile?.id);
        setDownloadRequestCtr(0);
      }
    }
  });

  const handleConfirm = async () => {
    setDialogButtonDisabled(true);
    setExportFileID(null);
    setDownloadStatus("loading");

    const result = await exportRemittanceOrders({
      variables: {
        input: {
          fileName: `Remittance${getMomentDate("_YYYYMMDDHHmmss")}`,
          fileType: FileTypesEnum.XLSX,
          filter: {
            ...filters
          },
          scope: ExportScope.FILTER
        }
      }
    });

    return result.data.exportRemittanceOrders.errors;
  };

  const {
    data: exportFileData,
    refetch: refetchExportFile
  } = useExportRemittanceOrderFile({
    displayLoader: true,
    skip: !!(
      exportFileID === null ||
      exportFileID === undefined ||
      exportFileID === ""
    ),
    variables: { id: exportFileID }
  });

  React.useEffect(() => {
    if (downloadRequestCtr > MAX_DOWNLOAD_RETRIES) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Download Failed"
        })
      });
      setDownloadStatus("error");
      closeModal();
      setDialogButtonDisabled(false);
      return;
    }
    if (exportFileData?.exportFile !== undefined) {
      if (exportFileData?.exportFile?.status === JobStatusEnum.SUCCESS) {
        window.open(
          exportFileData?.exportFile?.url,
          "_blank",
          "noopener,noreferrer"
        );
        setDownloadStatus("success");
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Remittance successfully downloaded."
          })
        });
        closeModal();
        setDialogButtonDisabled(false);
      } else if (exportFileData?.exportFile?.status === JobStatusEnum.FAILED) {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "Download Failed"
          })
        });
        setDownloadStatus("error");
        closeModal();
        setDialogButtonDisabled(false);
      } else {
        setDownloadRequestCtr(downloadRequestCtr + 1);
        setDownloadStatus("loading");
        refetchExportFile();
      }
    }
  }, [exportFileData]);

  return (
    <ActionDialog
      open={params.action === "download"}
      onClose={cancelExport}
      confirmButtonState={downloadStatus}
      disabled={dialogButtonDisabled}
      onConfirm={handleConfirm}
      title={intl.formatMessage({
        defaultMessage: "Download Remittance Reports",
        description: "dialog title"
      })}
    >
      <DialogContentText>
        <FormattedMessage
          defaultMessage="You are about to download report with {total} rows. Do you want to continue?"
          values={{
            total: <strong>{totalRows}</strong>
          }}
        />
      </DialogContentText>
    </ActionDialog>
  );
};

export default RemittanceRunnerDownloadDialog;
